/**
 * One public chapter for the opening and the card.
 * Facts come from the pack text. Phones and homepage dumps stay out.
 */
import type { UiLocale } from '../core/contracts.js'
import { hasEmergencySiteSignal } from '../core/intelligence/grounded-research-claims.js'

const LEGAL_SUFFIX = new Set(['AS', 'ASA', 'DA', 'ANS', 'NUF', 'SA', 'BA', 'KS', 'ENK', 'AB'])
const PLACES = ['Oslo', 'Lillestrøm', 'Bergen', 'Trondheim', 'Stavanger', 'Frogner'] as const

export interface PublicChapter {
  lines: string[]
  aiAngle?: string
}

function tidy(value: string | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim()
}

function titleCaseShoutedWord(word: string): string {
  const suffixBare = word.replace(/[^A-Za-zÆØÅæøå]/g, '')
  if (LEGAL_SUFFIX.has(suffixBare.toLocaleUpperCase('nb'))) {
    return word.replace(suffixBare, suffixBare.toLocaleUpperCase('nb'))
  }
  // TEST-CU stays TEST-CU. Ordinary shouted words become Havnevik.
  if (word.includes('-')) {
    const parts = word.split('-')
    const shouted = parts.every(part => {
      const letters = part.replace(/[^A-Za-zÆØÅæøå]/g, '')
      return letters.length > 0 && letters === letters.toLocaleUpperCase('nb') && !/[a-zæøå]/.test(letters)
    })
    if (shouted) return word
  }
  const lower = word.toLocaleLowerCase('nb')
  if (!lower) return word
  return lower.charAt(0).toLocaleUpperCase('nb') + lower.slice(1)
}

/** Registry names arrive in capitals. Mixed-case names stay as stored. */
export function humanizeFirmName(name: string): string {
  const trimmed = tidy(name)
  const letters = trimmed.replace(/[^A-Za-zÆØÅæøå]/g, '')
  if (letters.length < 3) return trimmed
  const upper = letters.replace(/[^A-ZÆØÅ]/g, '').length
  if (upper / letters.length < 0.8) return trimmed
  return trimmed.split(' ').map(titleCaseShoutedWord).join(' ')
}

function namedPlace(text: string): string | undefined {
  for (const place of PLACES) {
    if (new RegExp(`\\b${place}\\b`, 'i').test(text)) return place
  }
  return undefined
}

function isPlumbing(text: string): boolean {
  return /rørlegger|rorlegger|plumber|plumbing/i.test(text)
}

function joinFacts(facts: string[], locale: UiLocale): string | undefined {
  if (!facts.length) return undefined
  if (facts.length === 1) return facts[0]
  const glue = locale === 'nb' ? ' og ' : ' and '
  return `${facts.slice(0, -1).join(', ')}${glue}${facts[facts.length - 1]}`
}

/**
 * Who/where plus one sourced-fact sentence.
 * Returns nothing when the text has no place, mesterbedrift, or emergency coverage,
 * so a plain industry label can keep the short acknowledgment.
 */
export function publicChapter(input: {
  locale: UiLocale
  companyName?: string | undefined
  sector?: string | undefined
  text?: string | undefined
}): PublicChapter {
  const firm = humanizeFirmName(input.companyName || '')
  const blob = [input.sector, input.text].filter(Boolean).join('\n')
  const place = namedPlace(blob)
  const trade = isPlumbing(blob)
  const emergency = hasEmergencySiteSignal(blob)
  const master = /mesterbedrift/i.test(blob)
  if (!place && !emergency && !master) return { lines: [] }

  const lines: string[] = []
  if (input.locale === 'nb') {
    if (firm && trade && place) lines.push(`${firm} er et rørleggerfirma i ${place}-området.`)
    else if (firm && trade) lines.push(`${firm} er et rørleggerfirma.`)
    else if (firm && place) lines.push(`${firm} er omtalt i ${place}-området.`)
    const facts = joinFacts(
      [
        ...(master ? ['omtaler seg som mesterbedrift'] : []),
        ...(emergency ? ['fremhever 24-timers akuttvakt / døgnåpen beredskap'] : []),
      ],
      'nb',
    )
    if (facts) lines.push(`Nettstedet ${facts}.`)
    return {
      lines: lines.slice(0, 2),
      ...(emergency
        ? { aiAngle: 'Et lite triage-utkast som sorterer akutthenvendelser før noen ringer tilbake.' }
        : {}),
    }
  }

  if (firm && trade && place) lines.push(`${firm} is a plumbing firm in the ${place} area.`)
  else if (firm && trade) lines.push(`${firm} is a plumbing firm.`)
  else if (firm && place) lines.push(`${firm} shows up in the ${place} area.`)
  const facts = joinFacts(
    [
      ...(master ? ['present the firm as a mesterbedrift'] : []),
      ...(emergency ? ['lead with 24-hour emergency coverage'] : []),
    ],
    'en',
  )
  if (facts) lines.push(`Public pages ${facts}.`)
  return {
    lines: lines.slice(0, 2),
    ...(emergency
      ? {
          aiAngle: 'A small intake triage draft that ranks emergency calls before a human calls back.',
        }
      : {}),
  }
}
